import { useEffect, useState } from 'react'
import { useBsky } from '../store/bsky'
import { useAuth } from '../store/auth'
import { getProfile, setName as apiSetName } from '../api/gg'

/** Two-step gate for the play modes:
 *  1) connect a Bluesky account (app password) — content is pulled through it;
 *  2) pick a "goon name" for the Score Board (we never show your handle).
 *  Renders children once both are done. */
export function ConnectGate({ children }: { children: React.ReactNode }) {
  const session = useBsky((s) => s.session)
  const ready = useBsky((s) => s.ready)
  const token = useAuth((s) => s.token)
  const loginBluesky = useAuth((s) => s.loginBluesky)

  // undefined = loading, null = none yet, string = chosen
  const [alias, setAlias] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    if (!session || !token) return
    let live = true
    setAlias(undefined)
    getProfile(token)
      .then((p) => live && setAlias(p.name))
      .catch(() => live && setAlias(null))
    return () => {
      live = false
    }
  }, [session, token])

  if (!ready)
    return <Loading />
  if (!session) return <ConnectStep onConnect={loginBluesky} />
  if (alias === undefined) return <Loading />
  if (!alias) return <NameStep token={token!} onDone={setAlias} />
  return <>{children}</>
}

function Loading() {
  return <div className="grid min-h-[60vh] place-items-center font-comic text-2xl text-muted">…</div>
}

function ConnectStep({
  onConnect,
}: {
  onConnect: (service: string, identifier: string, password: string) => Promise<void>
}) {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function connect(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await onConnect('https://bsky.social', identifier.trim(), password)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'connection failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="grid min-h-[70vh] place-items-center px-4 py-8">
      <div className="panel panel-pink w-full max-w-md p-6">
        <h1 className="goon-title text-3xl">Connect Bluesky</h1>
        <p className="mt-2 text-sm leading-relaxed text-[#cfcfe6]">
          GoonGallery pulls <b className="text-pow">explicit NSFW media</b> through your Bluesky account — your rate budget. Pick your tags. Goon the feed. We never see your password.
        </p>
        <div className="mt-3 rounded-lg border border-kapow/40 bg-kapow/5 p-3 text-xs leading-relaxed text-[#bfeffb]">
          <span className="font-bold uppercase tracking-wide text-kapow">Heads up — rate limits.</span>{' '}
          Bluesky caps how fast any app can fetch. Big loads are paced; if you see a
          “rate limit — pausing” message, wait a few seconds and it keeps going.
        </div>
        <form onSubmit={connect} className="mt-4 space-y-2.5">
          <input
            className="input"
            placeholder="handle.bsky.social"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
          <input
            className="input"
            type="password"
            placeholder="app password  (xxxx-xxxx-xxxx-xxxx)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {err ? <div className="text-sm font-semibold text-pow neon-pink">{err}</div> : null}
          <button
            type="submit"
            disabled={busy || !identifier || !password}
            className="btn-comic w-full disabled:opacity-40"
          >
            {busy ? 'Connecting…' : 'Connect & Play'}
          </button>
        </form>
        <p className="mt-3 text-center text-[11px] leading-relaxed text-muted">
          Use a Bluesky{' '}
          <a
            href="https://bsky.app/settings/app-passwords"
            target="_blank"
            rel="noreferrer"
            className="text-kapow underline"
          >
            App Password
          </a>{' '}
          — not your main password.
        </p>
      </div>
    </main>
  )
}

const NAME_RE = /[^A-Za-z0-9_-]/g

function NameStep({ token, onDone }: { token: string; onDone: (name: string) => void }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const res = await apiSetName(token, name)
      onDone(res.name)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'try another name')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="grid min-h-[70vh] place-items-center px-4 py-8">
      <div className="panel panel-cyan w-full max-w-md p-6">
        <h1 className="comic-title comic-title-cyan text-3xl">Pick your goon name</h1>
        <p className="mt-2 text-sm leading-relaxed text-[#cfcfe6]">
          This is what shows on the board — <b className="text-white">not your handle</b>. Letters, numbers, <span className="font-mono text-kapow">-</span> and <span className="font-mono text-kapow">_</span> only. Example: GOONER_GUY_69
        </p>
        <form onSubmit={save} className="mt-4 space-y-2.5">
          <input
            className="input text-center font-comic text-2xl tracking-wide"
            placeholder="BIGGOONER_99"
            maxLength={20}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value.replace(NAME_RE, '').slice(0, 20))}
          />
          {err ? <div className="text-sm font-semibold text-pow neon-pink">{err}</div> : null}
          <button
            type="submit"
            disabled={busy || name.length < 3}
            className="btn-comic w-full disabled:opacity-40"
          >
            {busy ? 'Checking…' : 'Lock it in'}
          </button>
        </form>
        <p className="mt-3 text-center text-[11px] text-muted">3–20 characters · kept clean &amp; unique.</p>
      </div>
    </main>
  )
}
